/**
 * EditGuidelineDialog — edit + save an existing guideline.
 *
 * The eastviewos analogue of digital_sizer's "Open editor" (GuidelineEditorModal),
 * minus the heavy Tiptap / bound-table / revision machinery. The user polishes
 * the raw markdown with a live preview (same GuidelineMarkdown renderer as the
 * reader), tweaks title / program, optionally notes a change summary, then
 * saves via PUT /guidelines/:slug — which records a new revision and bumps the
 * version server-side.
 */
import { useCallback, useEffect, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import CloseRounded from "@mui/icons-material/CloseRounded";
import FullscreenRounded from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRounded from "@mui/icons-material/FullscreenExitRounded";
import Tooltip from "@mui/material/Tooltip";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { guidelinesApi } from "../../../api/guidelinesApi";
import GuidelineMarkdown from "./GuidelineMarkdown";

const PROGRAMS = ["RTL", "GUC", "DSCR"];

const errMessage = (e) =>
  e?.response?.data?.detail ||
  e?.response?.data?.error ||
  e?.message ||
  "Something went wrong";

const LABEL_SX = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#94a3b8",
  mb: 0.5,
};

export default function EditGuidelineDialog({ open, slug, doc, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fullScreen, setFullScreen] = useState(false);
  const [title, setTitle] = useState("");
  const [program, setProgram] = useState("RTL");
  const [markdown, setMarkdown] = useState("");
  const [changeSummary, setChangeSummary] = useState("");

  // Seed the form from the current doc whenever the dialog opens (or the
  // underlying doc changes).
  useEffect(() => {
    if (!open || !doc) return;
    setError(null);
    setSaving(false);
    setTitle(doc.title || "");
    setProgram(doc.program || "RTL");
    setMarkdown(doc.markdown || "");
    setChangeSummary("");
  }, [open, doc]);

  const handleClose = useCallback(() => {
    if (saving) return;
    onClose();
  }, [saving, onClose]);

  const handleSave = useCallback(async () => {
    if (!title.trim()) {
      setError("Title is required.");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const res = await guidelinesApi.update(slug, {
        title: title.trim(),
        program,
        markdown,
        change_summary: changeSummary.trim() || undefined,
      });
      onSaved(slug, res?.version);
    } catch (e) {
      setError(`Save failed: ${errMessage(e)}`);
      setSaving(false);
    }
  }, [slug, title, program, markdown, changeSummary, onSaved]);

  const programOptions = PROGRAMS.includes(program)
    ? PROGRAMS
    : [program, ...PROGRAMS];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          height: fullScreen ? "100%" : "88vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Flx
        ac
        jb
        g={1}
        sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #d8dee4", flex: "0 0 auto" }}
      >
        <Flx ac g={1} sx={{ minWidth: 0 }}>
          <Txt sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
            Edit guideline
          </Txt>
          {doc && (
            <Txt sx={{ fontSize: 12, color: "#94a3b8" }}>
              {slug} · v{doc.version}
            </Txt>
          )}
        </Flx>
        <Flx ac g={0.5}>
          <Tooltip title={fullScreen ? "Exit full screen" : "Full screen"} arrow>
            <IconButton size="small" onClick={() => setFullScreen((v) => !v)}>
              {fullScreen ? (
                <FullscreenExitRounded sx={{ fontSize: 20 }} />
              ) : (
                <FullscreenRounded sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={handleClose} disabled={saving}>
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>
      </Flx>

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Flx g={1.5} wrap sx={{ mb: 1.5 }}>
          <TextField
            label="Title"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            sx={{ flex: "2 1 260px" }}
            required
          />
          <TextField
            select
            label="Program"
            size="small"
            value={program}
            onChange={(e) => setProgram(e.target.value)}
            sx={{ flex: "0 1 140px", minWidth: 120 }}
          >
            {programOptions.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Change summary (optional)"
            size="small"
            value={changeSummary}
            onChange={(e) => setChangeSummary(e.target.value)}
            sx={{ flex: "2 1 240px" }}
            placeholder="What changed?"
            InputLabelProps={{ shrink: true }}
          />
        </Flx>

        {/* Split editor: raw markdown (left) · live preview (right) */}
        <Flx g={1.5} sx={{ flex: 1, minHeight: 0 }}>
          <Flx column sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <Txt sx={LABEL_SX}>Markdown</Txt>
            <TextField
              multiline
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              sx={{
                flex: 1,
                minHeight: 0,
                "& .MuiInputBase-root": {
                  height: "100%",
                  alignItems: "stretch",
                  p: 0,
                  overflow: "hidden",
                },
                "& .MuiInputBase-inputMultiline": {
                  height: "100% !important",
                  overflowY: "auto !important",
                  boxSizing: "border-box",
                  px: 1.75,
                  py: 1.5,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: 12.5,
                  lineHeight: 1.6,
                },
              }}
            />
          </Flx>
          <Flx column sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <Txt sx={LABEL_SX}>Preview</Txt>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: 1.5,
                px: 2.5,
                py: 2,
                bgcolor: "#fff",
              }}
            >
              <GuidelineMarkdown markdown={markdown} />
            </Box>
          </Flx>
        </Flx>
      </Box>

      {/* Footer */}
      <Flx
        ac
        end
        g={1}
        sx={{ px: 2.5, py: 1.75, borderTop: "1px solid #d8dee4", flex: "0 0 auto" }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={saving}
          sx={{
            textTransform: "none",
            color: "#64748b",
            borderColor: "#cbd5e1",
            "&:hover": { borderColor: "#94a3b8", bgcolor: "#f1f5f9" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSave}
          disabled={saving || !title.trim()}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 2.5,
            bgcolor: "#1F5AF2",
            boxShadow: "0 1px 3px rgba(20,80,107,0.3)",
            "&:hover": { bgcolor: "#1648CC" },
          }}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </Flx>
    </Dialog>
  );
}
